import { useEffect, useMemo, useState } from "react";
import Avatar from "react-avatar";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { getFriends } from "../Data/FriendshipHelpers.jsx";
import { normalizeAvatarSrc } from "../Components/avatarUtils";
import "../styles/pages/friends-leaderboard.scss";

const SPORTS = ["All sports", "NHL", "MLB", "NBA", "NFL"];

const RivalryMode = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [selectedFriendId, setSelectedFriendId] = useState("");
  const [sport, setSport] = useState("All sports");
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRivalries = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!authData.user) throw new Error("Please sign in to see your rivalries.");

      const { data: user, error: userError } = await supabase
        .from("users")
        .select("public_user_id, username, avatar")
        .eq("public_user_id", authData.user.id)
        .single();
      if (userError) throw userError;
      setCurrentUser(user);

      const friendList = await getFriends(user.public_user_id);
      setFriends(friendList);
      setSelectedFriendId((previous) => previous || friendList[0]?.public_user_id || "");
    } catch (loadError) {
      console.error("Could not load Rivalry Mode:", loadError);
      setError(loadError.message || "Could not load your rivalries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRivalries();
  }, []);

  useEffect(() => {
    if (!currentUser?.public_user_id || !selectedFriendId) {
      setBets([]);
      return undefined;
    }

    let cancelled = false;
    const loadSettledBets = async () => {
      setLoading(true);
      setError("");
      try {
        const [asCreator, asFriend] = await Promise.all([
          supabase
            .from("bets")
            .select("betid, gameid, gametitle, betcreator, creator_id, friend_id, result, sporttype, wager, betdescription, created_at")
            .in("betstatus", ["settled", "archived"])
            .eq("creator_id", currentUser.public_user_id)
            .eq("friend_id", selectedFriendId),
          supabase
            .from("bets")
            .select("betid, gameid, gametitle, betcreator, creator_id, friend_id, result, sporttype, wager, betdescription, created_at")
            .in("betstatus", ["settled", "archived"])
            .eq("creator_id", selectedFriendId)
            .eq("friend_id", currentUser.public_user_id),
        ]);
        if (asCreator.error) throw asCreator.error;
        if (asFriend.error) throw asFriend.error;
        if (!cancelled) {
          const uniqueBets = new Map();
          [...(asCreator.data || []), ...(asFriend.data || [])].forEach((bet) => uniqueBets.set(bet.betid, bet));
          setBets([...uniqueBets.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        }
      } catch (loadError) {
        console.error("Could not load rivalry results:", loadError);
        if (!cancelled) setError(loadError.message || "Could not load settled matchups.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettledBets();
    return () => { cancelled = true; };
  }, [currentUser?.public_user_id, selectedFriendId]);

  const friend = friends.find((item) => item.public_user_id === selectedFriendId);
  const rivalry = useMemo(() => {
    const results = bets
      .filter((bet) => sport === "All sports" || bet.sporttype?.toUpperCase() === sport)
      .map((bet) => {
        const normalizedResult = (bet.result || "").toLowerCase();
        if (!normalizedResult.includes(" wins") && !normalizedResult.includes(" lost")) return null;
        const creatorWon = normalizedResult.includes(" wins");
        const winnerId = creatorWon ? bet.creator_id : bet.friend_id;
        return { ...bet, winnerId, wonByYou: winnerId === currentUser?.public_user_id };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const wins = results.filter((result) => result.wonByYou).length;
    const losses = results.length - wins;
    let streak = 0;
    const streakWinner = results[0]?.winnerId;
    if (streakWinner) {
      for (const result of results) {
        if (result.winnerId !== streakWinner) break;
        streak += 1;
      }
    }

    return { results, wins, losses, streak, streakWinner };
  }, [bets, currentUser?.public_user_id, sport]);

  const recentRecap = rivalry.results[0];
  let recap = "The rivalry is waiting for its first settled bet. Time to make a move!";
  if (recentRecap?.wonByYou) recap = `${currentUser?.username || "You"} took the latest bragging rights. ${friend?.username || "Your friend"} will want a rematch!`;
  else if (recentRecap) recap = `${friend?.username || "Your friend"} won the latest round. Your comeback story starts now.`;

  const activeStreakName = rivalry.streakWinner === currentUser?.public_user_id
    ? currentUser?.username || "You"
    : friend?.username || "Your friend";

  return (
    <main className="fb-page friends-leaderboard-page">
      <section className="friends-leaderboard-panel fb-card">
        <header className="friends-leaderboard-header">
          <p className="fb-page__eyebrow">Head-to-head history</p>
          <h1>Rivalry Mode</h1>
          <p>Pick a friend, track the wins, and settle the bragging rights.</p>
        </header>

        <div className="friends-leaderboard-filters">
          <label>
            <span>Your rival</span>
            <select className="form-select" value={selectedFriendId} onChange={(event) => setSelectedFriendId(event.target.value)} disabled={!friends.length}>
              {friends.length ? friends.map((item) => <option key={item.public_user_id} value={item.public_user_id}>{item.username || "Friend"}</option>) : <option value="">No friends yet</option>}
            </select>
          </label>
          <label>
            <span>Sport</span>
            <select className="form-select" value={sport} onChange={(event) => setSport(event.target.value)}>
              {SPORTS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>

        {loading ? (
          <div className="friends-leaderboard-empty" role="status">Loading rivalry…</div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : !friend ? (
          <div className="friends-leaderboard-empty">
            <span aria-hidden="true">🤝</span>
            <h2>Find your first rival</h2>
            <p>Add friends to start building a head-to-head record.</p>
            <Link className="fb-btn fb-btn--primary mt-3" to="/addFriends">Add friends</Link>
          </div>
        ) : (
          <>
            <section className="rivalry-scoreboard" aria-label="All-time head-to-head record">
              <div className="rivalry-player">
                <Avatar round size="64" name={currentUser?.username || "You"} src={normalizeAvatarSrc(currentUser?.avatar)} />
                <strong>{currentUser?.username || "You"}</strong>
                <span>You</span>
              </div>
              <div className="rivalry-score">
                <span>ALL-TIME RECORD</span>
                <strong>{rivalry.wins} <i>—</i> {rivalry.losses}</strong>
                <small>{rivalry.results.length} settled {rivalry.results.length === 1 ? "bet" : "bets"}</small>
              </div>
              <div className="rivalry-player">
                <Avatar round size="64" name={friend.username || "Friend"} src={normalizeAvatarSrc(friend.avatar)} />
                <strong>{friend.username || "Friend"}</strong>
                <span>Your rival</span>
              </div>
            </section>

            <section className="rivalry-recap" aria-live="polite">
              <span className="rivalry-recap__icon" aria-hidden="true">{rivalry.streak > 1 ? "🔥" : "⚡"}</span>
              <div>
                <strong>{rivalry.streak ? `${activeStreakName} is on a ${rivalry.streak}-bet streak` : "The rivalry is on"}</strong>
                <p>{recap}</p>
              </div>
            </section>

            <div className="friends-leaderboard-caption">
              <strong>Recent matchups</strong>
              <span>{sport === "All sports" ? "All sports" : sport}</span>
            </div>
            {rivalry.results.length ? (
              <div className="rivalry-matchups">
                {rivalry.results.slice(0, 12).map((bet) => (
                  <article className="rivalry-matchup" key={bet.betid}>
                    <div className={`rivalry-matchup__result ${bet.wonByYou ? "is-win" : "is-loss"}`} aria-label={bet.wonByYou ? "You won" : `${friend.username} won`}>
                      {bet.wonByYou ? "W" : "L"}
                    </div>
                    <div className="rivalry-matchup__details">
                      <strong>{bet.gametitle || "Friendly matchup"}</strong>
                      <span>{bet.sporttype || "Sport"} · {new Date(bet.created_at).toLocaleDateString()}</span>
                      <small>{Object.keys(bet.betdescription || {}).filter((key) => bet.betdescription[key]).join(" · ") || bet.wager || "Settled bet"}</small>
                    </div>
                    <div className="rivalry-matchup__winner">{bet.wonByYou ? currentUser?.username : friend.username}</div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="friends-leaderboard-empty">
                <span aria-hidden="true">🏒</span>
                <h2>No settled matchups yet</h2>
                <p>Once one of your bets is settled, this is where the rivalry history starts.</p>
              </div>
            )}
          </>
        )}
        <footer className="friends-leaderboard-footer">
          <span>Rivalry records include settled and archived bets.</span>
          <button className="fb-btn fb-btn--ghost" type="button" onClick={loadRivalries} disabled={loading}>Refresh</button>
        </footer>
      </section>
    </main>
  );
};

export default RivalryMode;
